import type { ApiResponse } from '@gatheredhq/types';
import { apiFetch } from './client';

export interface CopyCounts {
  menus: number;
  categories: number;
  items: number;
  addonGroups: number;
  stories: number;
}

export interface CopyResult {
  counts: CopyCounts;
  settingsCopied: string[];
}

export type CopyPart = 'menu' | 'settings' | 'stories';

/**
 * Seed a venue from another venue in the same organization.
 *
 * Additive: it adds menus rather than replacing them. The server REFUSES by default when
 * an incoming menu's name is already in use at the target, throwing `ApiError` with code
 * `MENU_ALREADY_IMPORTED` — so a second click, after a slow first attempt that may have
 * succeeded, reports the duplicate instead of quietly creating one. Pass
 * `onDuplicate: 'rename'` once the owner has confirmed they want it anyway; the copy then
 * arrives as "Brunch (2)".
 */
export async function copyFromVenue(
  targetSlug: string,
  sourceSlug: string,
  parts: CopyPart[],
  /** Omit to bring everything of that kind — the shape used when seeding a new venue. */
  selection?: { menuIds?: string[]; storyIds?: string[] },
  onDuplicate: 'block' | 'rename' = 'block',
): Promise<CopyResult> {
  const res = await apiFetch<ApiResponse<CopyResult>>(`/api/venues/${targetSlug}/copy-from`, {
    method: 'POST',
    body: JSON.stringify({ sourceSlug, parts, ...selection, onDuplicate }),
  });
  if (!res.success || !res.data) throw new Error(res.error?.message ?? 'Failed to copy');
  return res.data;
}
